import React, { useState, useCallback } from 'react';
import {
  Modal,
  Stack,
  Title,
  Text,
  Group,
  Button,
  Divider,
  Paper,
  Box,
  Alert,
  Badge
} from '@mantine/core';
import { IconAlertCircle, IconCheck } from '@tabler/icons-react';
import { ConfirmMentionModalProps } from './ConfirmMentionModal.types';
import { BusinessSearchAutocomplete } from '../BusinessSearchAutocomplete';
import { StatusBadge } from '../StatusBadge';
import { SubredditBadge } from '../SubredditBadge';
import { ConfidenceIndicator } from '../ConfidenceIndicator';
import { BusinessSummary } from '../../../../domain/entities/Business';
import { useConfirmMention } from '../../../../application/queries/mentionQueries';

/**
 * ConfirmMentionModal Component
 *
 * Complete workflow for confirming a mention and linking it to a business
 *
 * Features:
 * - Display mention details (read-only)
 * - Business search with debounced autocomplete
 * - Preview selected business
 * - Confirm button (disabled until business selected)
 * - Loading state during mutation
 * - Auto-close on success
 *
 * Session 005: Created for confirm workflow
 *
 * Workflow:
 * 1. User clicks "Confirm" on mention
 * 2. Modal opens with mention details
 * 3. User searches and selects business
 * 4. Preview shows selected business
 * 5. User clicks "Confirm Match"
 * 6. Mutation fires
 * 7. Success: modal closes, list auto-refreshes
 *
 * Usage:
 * ```tsx
 * <ConfirmMentionModal
 *   mention={selectedMention}
 *   opened={isOpen}
 *   onClose={() => setIsOpen(false)}
 * />
 * ```
 */
export const ConfirmMentionModal: React.FC<ConfirmMentionModalProps> = ({
  mention,
  opened,
  onClose
}): React.ReactElement => {
  const [selectedBusiness, setSelectedBusiness] = useState<BusinessSummary | null>(null);
  const confirmMutation = useConfirmMention();

  /**
   * Handle business selection from autocomplete
   */
  const handleBusinessSelect = useCallback((business: BusinessSummary | null): void => {
    setSelectedBusiness(business);
  }, []);

  /**
   * Handle confirm match
   */
  const handleConfirm = useCallback(async (): Promise<void> => {
    if (!selectedBusiness) {
      return;
    }

    try {
      await confirmMutation.mutateAsync({
        mentionId: mention.id,
        businessId: selectedBusiness.id
      });

      // Close modal on success (list will auto-refresh via React Query)
      setSelectedBusiness(null);
      onClose();
    } catch {
      // Error is already handled by mutation (notification shown)
      // Keep modal open so user can try again
    }
  }, [selectedBusiness, mention.id, confirmMutation, onClose]);

  /**
   * Handle modal close
   */
  const handleClose = useCallback((): void => {
    setSelectedBusiness(null);
    onClose();
  }, [onClose]);

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title={<Title order={3}>Confirm Mention</Title>}
      size="lg"
      padding="lg"
    >
      <Stack gap="md">
        {/* Mention Details (Read-only) */}
        <Alert icon={<IconAlertCircle size={16} />} color="blue" variant="light">
          Review the mention details below and select the correct business to confirm the match.
        </Alert>

        <Paper p="md" bg="gray.0" radius="md">
          <Stack gap="sm">
            {/* Post Title */}
            <Box>
              <Text size="xs" c="dimmed" fw={600} tt="uppercase" mb={4}>
                Post Title
              </Text>
              <Text size="sm" fw={500} lineClamp={2}>
                {mention.postTitle}
              </Text>
            </Box>

            {/* Metadata */}
            <Group>
              <Box>
                <Text size="xs" c="dimmed" fw={600} tt="uppercase" mb={4}>
                  Subreddit
                </Text>
                <SubredditBadge subreddit={mention.subreddit} size="sm" />
              </Box>

              <Box>
                <Text size="xs" c="dimmed" fw={600} tt="uppercase" mb={4}>
                  Status
                </Text>
                <StatusBadge status={mention.reviewStatus as any} size="sm" />
              </Box>

              {mention.matchConfidenceScore !== null && mention.matchConfidenceScore > 0 && (
                <Box>
                  <Text size="xs" c="dimmed" fw={600} tt="uppercase" mb={4}>
                    Confidence
                  </Text>
                  <ConfidenceIndicator
                    score={mention.matchConfidenceScore}
                    variant="badge"
                    size="sm"
                  />
                </Box>
              )}
            </Group>

            {/* Content Preview */}
            <Box>
              <Text size="xs" c="dimmed" fw={600} tt="uppercase" mb={4}>
                Content Preview
              </Text>
              <Text size="sm" lineClamp={3}>
                {mention.content}
              </Text>
            </Box>
          </Stack>
        </Paper>

        <Divider label="Select Business" labelPosition="center" />

        {/* Business Search */}
        <BusinessSearchAutocomplete
          value={selectedBusiness}
          onChange={handleBusinessSelect}
          label="Business"
          placeholder="Search for a business..."
          description="Type at least 2 characters to search"
          required
        />

        {/* Business Preview */}
        {selectedBusiness && (
          <Paper p="md" withBorder radius="md">
            <Stack gap="xs">
              <Text size="xs" c="dimmed" fw={600} tt="uppercase" mb={4}>
                Selected Business
              </Text>
              <Group justify="space-between" align="start">
                <Box>
                  <Text size="md" fw={600}>
                    {selectedBusiness.name}
                  </Text>
                  {selectedBusiness.category && (
                    <Text size="sm" c="dimmed">
                      {selectedBusiness.category}
                    </Text>
                  )}
                  {(selectedBusiness.formattedAddress || selectedBusiness.addressLine1 || selectedBusiness.city) && (
                    <Text size="sm" c="dimmed" mt={4}>
                      {selectedBusiness.formattedAddress ||
                        [selectedBusiness.addressLine1, selectedBusiness.city, selectedBusiness.state]
                          .filter(Boolean)
                          .join(', ')}
                    </Text>
                  )}
                </Box>
                {selectedBusiness.regionName && (
                  <Badge variant="light" color="violet">
                    {selectedBusiness.regionName}
                  </Badge>
                )}
              </Group>
            </Stack>
          </Paper>
        )}

        <Divider />

        {/* Actions */}
        <Group justify="space-between">
          <Button variant="default" onClick={handleClose} disabled={confirmMutation.isPending}>
            Cancel
          </Button>

          <Button
            leftSection={<IconCheck size={16} />}
            color="green"
            onClick={handleConfirm}
            disabled={!selectedBusiness}
            loading={confirmMutation.isPending}
          >
            Confirm Match
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};
